import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState, useRef } from 'react';
import { State } from 'redux/initialState';

import { Envelope } from 'types';

import { getEnvelopes } from 'modules/_shared/redux/envelopeActions';
import { setEnvelopes } from 'modules/envelopes/redux/envelopeActions';

const CACHE_DURATION = 5 * 60 * 1000;

const cacheTimestamps = new Map<string, number>();
const cacheData = new Map<string, any[]>();

export const useGetEnvelopesList = (
  societyId: string | undefined,
  pagination?: { page?: number; limit?: number },
  filters?: { status?: string; signatureType?: string }
) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const previousSocietyId = useRef<string | undefined>();

  const envelopes = useSelector((state: State) => state.envelopes);

  const clearCache = useCallback((societyIdToClear?: string) => {
    if (societyIdToClear) {
      cacheTimestamps.delete(societyIdToClear);
      cacheData.delete(societyIdToClear);
    } else {
      cacheTimestamps.clear();
      cacheData.clear();
    }
  }, []);

  const fetchEnvelopesList = useCallback(
    async (forceRefresh = false) => {
      if (!societyId) {
        return;
      }

      const page = pagination?.page || 1;
      const limit = pagination?.limit || 10;
      const filterParams = filters || {};
      const cacheKey = `${societyId}:${page}:${limit}:${
        filterParams.status || 'all'
      }:${filterParams.signatureType || 'all'}`;

      const lastFetchTime = cacheTimestamps.get(cacheKey);
      const cachedEnvelopes = cacheData.get(cacheKey);
      const now = Date.now();
      const isDataFresh = lastFetchTime && now - lastFetchTime < CACHE_DURATION;
      const hasCachedData = cachedEnvelopes && cachedEnvelopes.length > 0;

      if (isDataFresh && hasCachedData && !forceRefresh) {
        dispatch(setEnvelopes(cachedEnvelopes));
        return;
      }

      try {
        setIsLoading(true);
        setIsError(false);

        const response = (await getEnvelopes(
          societyId,
          { page, limit },
          filterParams
        )) as any;

        const envelopesData: Envelope[] = Array.isArray(response)
          ? (response as Envelope[])
          : (response?.items as Envelope[]);

        const totalFromResponse: number | undefined = Array.isArray(response)
          ? undefined
          : (response?.total as number);

        dispatch(setEnvelopes(envelopesData));

        cacheTimestamps.set(cacheKey, now);
        cacheData.set(cacheKey, envelopesData);

        if (typeof totalFromResponse === 'number') {
          cacheData.set(`${societyId}__total`, [totalFromResponse]);
        }
      } catch (error) {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [societyId, dispatch, pagination?.page, pagination?.limit, filters]
  );

  useEffect(() => {
    if (previousSocietyId.current && previousSocietyId.current !== societyId) {
      clearCache(previousSocietyId.current);
    }
    previousSocietyId.current = societyId;

    fetchEnvelopesList();
  }, [fetchEnvelopesList, clearCache, societyId]);

  return {
    data: {
      envelopes,
      envelopesStatus: envelopes
        .map((env) => env.envelopeStatus)
        .filter(Boolean),
      envelopesTotal: (cacheData.get(`${societyId as string}__total`) || [
        envelopes.length,
      ])[0],
    },
    isLoading,
    isError,
    refetch: () => fetchEnvelopesList(true),
    clearCache,
  };
};
